// Ballistic attack arcs (SPEC §15).
//
// "The map must read as a war, at a glance, without a legend. A grid of coloured
// squares does not. Tracer arcs between countries do."
//
// The shape of this file follows the shape of the data. EPHEMERAL already decodes
// flows into PARALLEL TYPED ARRAYS precisely so the arc pass can upload them as
// instanced vertex attributes with no repacking, so everything here is either
// (a) pure arithmetic over those arrays, tested without a GL context, or
// (b) one instanced draw call that evaluates the Bézier in the vertex shader.
//
// Four decisions worth stating up front, because each is the difference between
// arcs that read as artillery and arcs that read as a node graph:
//
//	LIFT SCALES WITH CHORD.  p1 is the chord's midpoint pushed perpendicular by
//	  ~0.22 x |p2-p0|, clamped at both ends. A two-pixel skirmish is a flat hop;
//	  a transcontinental strike lobs high. A constant lift makes every shot the
//	  same shape, which is exactly what a graph edge looks like.
//
//	SCREEN-SPACE WIDTH.  The strip is expanded along the curve normal in DEVICE
//	  pixels, so stroke thickness is zoom-independent and the geometry never has
//	  to be regenerated. Board-space width would make a world-zoom tracer thinner
//	  than a pixel.
//
//	ADDITIVE BLEND.  A contested front self-illuminates as tracers stack. The map
//	  gets visibly hotter where the war is, with no extra logic, no heat map and
//	  no second data channel.
//
//	ATTACKER'S COLOUR.  Not the defender's. A front then reads directionally —
//	  you can see which way the fire is going without reading a single label.
//
// The concurrency cap (tokens.json, `arc.maxConcurrent`) is not a dropped-frames
// budget, it is a legibility budget: a map where every click is a tracer is a
// white screen. The flows that lose the cut are folded into per-chunk FRONT GLOW
// rather than discarded, so a thousand skirmishes on one front still light it up.

import { BoardH, BoardW, ChunkSize, ChunksX, ChunksY, type ChunkID, type PixelID } from '../wire/core';
import {
  FlowBeachhead,
  FlowFortressSiege,
  FlowKill,
  TierChunk,
  TierPixel,
  type Ephemeral,
} from '../wire/ephemeral';
import type { Camera } from './camera';
import type { RGB } from './palette';
import tokens from '../tokens.json';

// --- constants, from tokens.json ------------------------------------------

/** §15's `0.22 x |p2 - p0|`, and the file that both this and the Go share card read. */
export const ArcLiftRatio = tokens.arc.liftRatio;
/** The top ~250 flows by weight are drawn; the rest become front glow. */
export const MaxConcurrentArcs = tokens.arc.maxConcurrent;
/** How long the tracer takes to travel the curve. */
export const TracerMs = tokens.motion.tracerMs;
/** How long the stroke takes to fade after impact. */
export const ArcFadeMs = tokens.motion.arcFadeMs;
/** "A kill flash is brighter and holds ~200 ms longer." */
export const KillHoldMs = 200;

/**
 * The clamp on lift, in board pixels.
 *
 * The lower bound is not cosmetic: at a chord of zero the perpendicular is
 * undefined, and a flow whose source and target are the same pixel (a fortify, or
 * a coalesced shot at an adjacent tile) still needs a curve with a direction. The
 * upper bound stops a corner-to-corner strike — chord ~452 — from lobbing 100
 * board pixels off the top of the map, where the whole arc is off screen at any
 * zoom that shows both ends.
 */
export const MinLift = 0.35;
export const MaxLift = 90;

/** How far off the board a beachhead's arc starts. §5's beachhead has no source pixel. */
export const BeachheadRunUp = 56;

/** Front glow outlives the arcs it stands in for, because it stands in for many of them. */
export const GlowMs = 2400;

// --- geometry -------------------------------------------------------------

export interface Vec2 {
  x: number;
  y: number;
}

/** The centre of a board pixel, which is where an arc endpoint belongs. */
export function pixelCentre(p: PixelID): Vec2 {
  return { x: (p % BoardW) + 0.5, y: ((p / BoardW) | 0) + 0.5 };
}

/**
 * The centre of a chunk.
 *
 * §15: at world zoom the same curve is drawn between chunk CENTROIDS from the
 * aggregated tier, so a thousand skirmishes on one front collapse into one thick
 * bright arc between two countries. Zooming in resolves it into individual shots.
 */
export function chunkCentre(c: ChunkID): Vec2 {
  return {
    x: (c % ChunksX) * ChunkSize + ChunkSize / 2,
    y: ((c / ChunksX) | 0) * ChunkSize + ChunkSize / 2,
  };
}

/**
 * The lift for a chord of this length, in board pixels.
 *
 * This one function is the whole ballistic read. Linear in the chord, clamped at
 * both ends, and deliberately NOT a square root or a log: trajectory intuition is
 * that twice as far lobs twice as high, and any curve that flattens with distance
 * makes long shots look like they were fired from a different weapon.
 */
export function arcLift(chord: number): number {
  const raw = ArcLiftRatio * chord;
  if (raw < MinLift) return MinLift;
  if (raw > MaxLift) return MaxLift;
  return raw;
}

export function chordLength(p0: Vec2, p2: Vec2): number {
  return Math.hypot(p2.x - p0.x, p2.y - p0.y);
}

/**
 * The quadratic Bézier's control point: the chord's midpoint, pushed along the
 * chord's perpendicular by `arcLift`.
 *
 * The rotation is CONSISTENT rather than always-upward. A -> B and B -> A then
 * bow to opposite sides, so on a contested front the outgoing and the incoming
 * fire separate into two visible lanes instead of stacking into one line — which
 * matters because the colour already says which direction each one is going, and
 * two overlapping arcs in two colours read as one muddy arc in neither.
 */
export function controlPoint(p0: Vec2, p2: Vec2): Vec2 {
  const dx = p2.x - p0.x;
  const dy = p2.y - p0.y;
  const len = Math.hypot(dx, dy);
  const lift = arcLift(len);
  if (len < 1e-6) return { x: p0.x, y: p0.y - lift };
  const nx = dy / len;
  const ny = -dx / len;
  return { x: p0.x + dx / 2 + nx * lift, y: p0.y + dy / 2 + ny * lift };
}

/** The point at `t` on the quadratic Bézier through p0, p1, p2. */
export function bezier(p0: Vec2, p1: Vec2, p2: Vec2, t: number): Vec2 {
  const u = 1 - t;
  const a = u * u;
  const b = 2 * u * t;
  const c = t * t;
  return { x: a * p0.x + b * p1.x + c * p2.x, y: a * p0.y + b * p1.y + c * p2.y };
}

/**
 * Where a beachhead's arc comes from.
 *
 * §5's beachhead is legal only while the attacker holds no pixels, so there IS no
 * source pixel and the wire says so with an explicit flag rather than a sentinel
 * coordinate — pixel 0 is the top-left corner of the board and would draw a real,
 * wrong arc. Off-map is the honest answer: a landing comes from outside the map,
 * so the arc starts outside the map, over the nearest edge.
 */
export function beachheadSource(dst: Vec2): Vec2 {
  const left = dst.x;
  const right = BoardW - dst.x;
  const top = dst.y;
  const bottom = BoardH - dst.y;
  const nearest = Math.min(left, right, top, bottom);
  if (nearest === left) return { x: -BeachheadRunUp, y: dst.y };
  if (nearest === right) return { x: BoardW + BeachheadRunUp, y: dst.y };
  if (nearest === top) return { x: dst.x, y: -BeachheadRunUp };
  return { x: dst.x, y: BoardH + BeachheadRunUp };
}

// --- the budget -----------------------------------------------------------

/** One chunk's residual fire: what the cap could not draw, kept as light. */
export interface GlowCell {
  chunk: ChunkID;
  attacker: number;
  /** Summed weight of every flow folded in here. */
  weight: number;
}

export interface ArcPlan {
  /** Indices into the flow arrays, heaviest first. */
  drawn: number[];
  /** Everything the cap refused, aggregated per (target chunk, attacker). */
  glow: GlowCell[];
}

function chunkOfXY(x: number, y: number): ChunkID {
  const cx = Math.min(ChunksX - 1, Math.max(0, Math.floor(x / ChunkSize)));
  const cy = Math.min(ChunksY - 1, Math.max(0, Math.floor(y / ChunkSize)));
  return cy * ChunksX + cx;
}

/**
 * Orders flow indices by weight, heaviest first, ties broken by arrival order.
 *
 * The tie-break is not decoration. Without it the sort is unstable across engines
 * and a front made of equal-weight single clicks would shimmer as the same 250 of
 * a thousand identical flows were re-chosen in a different order every tick.
 */
export function orderByWeight(weight: ArrayLike<number>, count: number): number[] {
  const idx: number[] = new Array(count);
  for (let i = 0; i < count; i++) idx[i] = i;
  idx.sort((a, b) => {
    const d = (weight[b] ?? 0) - (weight[a] ?? 0);
    return d !== 0 ? d : a - b;
  });
  return idx;
}

/**
 * Applies the concurrency cap: the heaviest `cap` flows are drawn, and every flow
 * below the cut is folded into its target chunk's glow.
 *
 * §15 is explicit that the remainder is folded rather than dropped "silently".
 * The distinction is visible: dropping them makes a heavily contested front look
 * QUIETER than a lightly contested one, because the light front's few arcs all
 * fit under the cap and the heavy front's thousand do not.
 */
export function planArcs(
  count: number,
  weight: ArrayLike<number>,
  attacker: ArrayLike<number>,
  targetChunk: (i: number) => ChunkID,
  cap = MaxConcurrentArcs,
): ArcPlan {
  const order = orderByWeight(weight, count);
  const drawn = order.slice(0, Math.max(0, cap));
  const rest = order.slice(Math.max(0, cap));
  return { drawn, glow: foldGlow(rest, weight, attacker, targetChunk) };
}

/** Aggregates flows by (target chunk, attacker). Order is chunk asc, attacker asc. */
export function foldGlow(
  indices: readonly number[],
  weight: ArrayLike<number>,
  attacker: ArrayLike<number>,
  targetChunk: (i: number) => ChunkID,
): GlowCell[] {
  const acc = new Map<number, GlowCell>();
  for (const i of indices) {
    const c = targetChunk(i);
    const a = attacker[i] ?? 0;
    const key = c * 65536 + a;
    const cell = acc.get(key);
    if (cell) cell.weight += weight[i] ?? 0;
    else acc.set(key, { chunk: c, attacker: a, weight: weight[i] ?? 0 });
  }
  return [...acc.values()].sort((x, y) => x.chunk - y.chunk || x.attacker - y.attacker);
}

// --- the live set ---------------------------------------------------------

/** Just enough of Palette for the arc pass, so a test can pass a two-line stub. */
export interface ArcPalette {
  colour(id: number): RGB;
}

/**
 * How hot a fortress siege burns, 0..1, rising as the fortress dies.
 *
 * §9/§15: "a fortress at 40% HP should be legible as a thing under attack from
 * across the map". The wire flags the siege but does not carry the HP, and the
 * client already holds it in BoardReplica — so this is a hook rather than a
 * guess, and arcs.ts stays free of the board.
 */
export type SiegeHeat = (target: PixelID) => number;

export interface Arc {
  x0: number;
  y0: number;
  x2: number;
  y2: number;
  lift: number;
  /** 0..1. */
  r: number;
  g: number;
  b: number;
  /** Normalised coalesced click count, 0..1. */
  weight: number;
  /** ms, relative to the field's epoch. */
  birth: number;
  flags: number;
  /** 0..1 siege heat, already resolved at ingest. */
  heat: number;
  /** Kept so an eviction folds into the right country's glow, not into a shared one. */
  attacker: number;
}

/**
 * One (chunk, attacker) cell of front glow.
 *
 * Keyed rather than listed, and that is the load-bearing part. A busy front sheds
 * hundreds of flows a tick and glow outlives the arcs it replaces, so a list
 * would grow without bound and — worse — would stack hundreds of additive quads
 * over the same chunk until the front was a white square. Merging by key makes
 * the count bounded by the board (a hundred chunks times the countries firing
 * into them) and makes the brightness a single clamped value.
 */
interface Glow {
  chunk: ChunkID;
  x: number;
  y: number;
  r: number;
  g: number;
  b: number;
  weight: number;
  birth: number;
}

/** Eleven floats per instance. Offsets are the vertex attribute layout. */
export const ARC_STRIDE_FLOATS = 11;
export const ARC_OFF_P0 = 0;
export const ARC_OFF_P2 = 2;
export const ARC_OFF_LIFT = 4;
export const ARC_OFF_COLOUR = 5;
export const ARC_OFF_WEIGHT = 8;
export const ARC_OFF_BIRTH = 9;
export const ARC_OFF_FLAGS = 10;

/** Seven floats per glow instance: centre, radius, colour, intensity. */
export const GLOW_STRIDE_FLOATS = 7;

export interface PackedInstances {
  data: Float32Array<ArrayBuffer>;
  count: number;
}

export interface ArcFieldOptions {
  cap?: number;
  siegeHeat?: SiegeHeat;
  /**
   * Float32 has a 24-bit mantissa, so a birth time carried as milliseconds since
   * the page loaded loses sub-millisecond resolution after about an hour and
   * whole milliseconds after five. A season is four weeks and this game's whole
   * point is a tab left open, so the epoch is rebased rather than assumed.
   */
  epochSpanMs?: number;
}

/**
 * Every arc currently on screen, and the front glow standing in for the ones that
 * did not fit.
 *
 * Ingest is per EPHEMERAL frame; pruning and packing are per render frame. The
 * two are separate because the tick is 500 ms and the display is not, and an arc
 * whose animation advanced only when a frame arrived would stutter at 2 Hz.
 */
export class ArcField {
  private arcs: Arc[] = [];
  private glow = new Map<number, Glow>();
  private epoch = 0;
  private haveEpoch = false;
  private readonly cap: number;
  private readonly heatOf: SiegeHeat;
  private readonly epochSpanMs: number;

  private arcScratch: Float32Array<ArrayBuffer> = new Float32Array(64 * ARC_STRIDE_FLOATS);
  private glowScratch: Float32Array<ArrayBuffer> = new Float32Array(32 * GLOW_STRIDE_FLOATS);
  /** Bumped whenever the set changes, so the layer re-uploads only when it must. */
  private rev = 0;

  constructor(opts: ArcFieldOptions = {}) {
    this.cap = opts.cap ?? MaxConcurrentArcs;
    this.heatOf = opts.siegeHeat ?? (() => 0.5);
    this.epochSpanMs = opts.epochSpanMs ?? 3_600_000;
  }

  count(): number {
    return this.arcs.length;
  }

  glowCount(): number {
    return this.glow.size;
  }

  revision(): number {
    return this.rev;
  }

  /** ms since the field's epoch, which is what the shader's `uNow` wants. */
  clock(now: number): number {
    if (!this.haveEpoch) return 0;
    return now - this.epoch;
  }

  /** The longest an arc can live: tracer, then fade, plus a kill's extra hold. */
  static lifeMs(flags: number): number {
    return TracerMs + ArcFadeMs + ((flags & FlowKill) !== 0 ? KillHoldMs : 0);
  }

  /**
   * Turns one EPHEMERAL frame into arcs.
   *
   * Both tiers land here and produce the same instance layout, which is what lets
   * the world view and the close view be one draw call and one shader rather than
   * two renderers that drift apart.
   */
  ingest(e: Ephemeral, palette: ArcPalette, now: number): void {
    if (!this.haveEpoch) {
      this.epoch = now;
      this.haveEpoch = true;
    } else if (now - this.epoch > this.epochSpanMs) {
      this.rebase(now);
    }
    const birth = now - this.epoch;

    if (e.tier === TierPixel && e.pixelFlows) {
      const f = e.pixelFlows;
      const plan = planArcs(
        f.count,
        f.weight,
        f.attacker,
        (i) => {
          const d = f.dst[i] ?? 0;
          return chunkOfXY(d % BoardW, (d / BoardW) | 0);
        },
        this.cap,
      );
      for (const i of plan.drawn) {
        const dst = pixelCentre(f.dst[i] ?? 0);
        const beach = ((f.flags[i] ?? 0) & FlowBeachhead) !== 0;
        const src = beach ? beachheadSource(dst) : pixelCentre(f.src[i] ?? 0);
        const c = palette.colour(f.attacker[i] ?? 0);
        this.arcs.push({
          x0: src.x,
          y0: src.y,
          x2: dst.x,
          y2: dst.y,
          lift: arcLift(chordLength(src, dst)),
          r: c.r / 255,
          g: c.g / 255,
          b: c.b / 255,
          // u8 weight, saturating at 255 on the wire. The curve is deliberately
          // not linear: the difference between one click and four is what a
          // player is looking at, and the difference between 200 and 255 is not.
          weight: normaliseWeight(f.weight[i] ?? 0, 255),
          birth,
          flags: f.flags[i] ?? 0,
          attacker: f.attacker[i] ?? 0,
          heat:
            ((f.flags[i] ?? 0) & FlowFortressSiege) !== 0 ? clamp01(this.heatOf(f.dst[i] ?? 0)) : 0,
        });
      }
      this.addGlow(plan.glow, palette, birth, 255);
    } else if (e.tier === TierChunk && e.chunkFlows) {
      const f = e.chunkFlows;
      const plan = planArcs(f.count, f.weight, f.attacker, (i) => f.dst[i] ?? 0, this.cap);
      for (const i of plan.drawn) {
        const src = chunkCentre(f.src[i] ?? 0);
        const dst = chunkCentre(f.dst[i] ?? 0);
        const c = palette.colour(f.attacker[i] ?? 0);
        this.arcs.push({
          x0: src.x,
          y0: src.y,
          x2: dst.x,
          y2: dst.y,
          lift: arcLift(chordLength(src, dst)),
          r: c.r / 255,
          g: c.g / 255,
          b: c.b / 255,
          weight: normaliseWeight(f.weight[i] ?? 0, 4096),
          birth,
          flags: 0,
          attacker: f.attacker[i] ?? 0,
          heat: 0,
        });
      }
      this.addGlow(plan.glow, palette, birth, 4096);
    }

    this.trim(birth);
    this.rev++;
  }

  private addGlow(cells: readonly GlowCell[], palette: ArcPalette, birth: number, scale: number): void {
    for (const cell of cells) {
      const c = palette.colour(cell.attacker);
      this.mergeGlow(cell.chunk, cell.attacker, c.r / 255, c.g / 255, c.b / 255, normaliseWeight(cell.weight, scale), birth);
    }
  }

  /**
   * Adds light to one (chunk, attacker) cell.
   *
   * Merging rather than appending is what keeps the glow bounded and, more to the
   * point, keeps it looking like glow: the same chunk hit five hundred times in a
   * tick is one brighter cell, not five hundred stacked additive quads that
   * saturate to a white square.
   */
  private mergeGlow(
    chunk: ChunkID,
    attacker: number,
    r: number,
    g: number,
    b: number,
    weight: number,
    birth: number,
  ): void {
    const key = chunk * 65536 + (attacker & 0xffff);
    const existing = this.glow.get(key);
    if (existing) {
      existing.weight = clamp01(existing.weight + weight);
      // Refreshed, not averaged: a front that is still being fired into should
      // stay lit, and a front that stopped should go out on its own.
      existing.birth = birth;
      return;
    }
    const p = chunkCentre(chunk);
    this.glow.set(key, { chunk, x: p.x, y: p.y, r, g, b, weight: clamp01(weight), birth });
  }

  /**
   * Holds the LIVE set to the cap, not merely each tick's share of it.
   *
   * An arc lives ~1.8 s and a tick is 500 ms, so four ticks of a busy front would
   * otherwise stack to four times the budget. Evictions are the lightest and
   * oldest, and they go to glow like any other flow that lost the cut.
   */
  private trim(birth: number): void {
    if (this.arcs.length <= this.cap) return;
    this.arcs.sort((a, b) => b.weight - a.weight || b.birth - a.birth);
    const evicted = this.arcs.splice(this.cap);
    for (const a of evicted) {
      // Folded into the target's chunk, exactly like a flow that lost the cut at
      // ingest. An eviction is the same event one tick later.
      this.mergeGlow(chunkOfXY(a.x2, a.y2), a.attacker, a.r, a.g, a.b, a.weight, birth);
    }
  }

  private rebase(now: number): void {
    const shift = now - this.epoch;
    for (const a of this.arcs) a.birth -= shift;
    for (const g of this.glow.values()) g.birth -= shift;
    this.epoch = now;
  }

  /** Drops arcs and glow that have finished. Call once a frame. */
  prune(now: number): void {
    if (!this.haveEpoch) return;
    const t = now - this.epoch;
    const beforeArcs = this.arcs.length;
    const beforeGlow = this.glow.size;
    this.arcs = this.arcs.filter((a) => t - a.birth <= ArcField.lifeMs(a.flags));
    for (const [key, g] of this.glow) {
      if (t - g.birth > GlowMs) this.glow.delete(key);
    }
    if (this.arcs.length !== beforeArcs || this.glow.size !== beforeGlow) this.rev++;
  }

  /** Forgets everything. A gap discards the board; the fire over it goes too. */
  clear(): void {
    this.arcs = [];
    this.glow.clear();
    this.rev++;
  }

  /** The arcs as instance attributes, in the layout the vertex shader declares. */
  packArcs(into?: Float32Array<ArrayBuffer>): PackedInstances {
    const need = this.arcs.length * ARC_STRIDE_FLOATS;
    const data = into && into.length >= need ? into : new Float32Array(Math.max(need, 64));
    for (let i = 0; i < this.arcs.length; i++) {
      const a = this.arcs[i]!;
      const at = i * ARC_STRIDE_FLOATS;
      data[at + ARC_OFF_P0] = a.x0;
      data[at + ARC_OFF_P0 + 1] = a.y0;
      data[at + ARC_OFF_P2] = a.x2;
      data[at + ARC_OFF_P2 + 1] = a.y2;
      data[at + ARC_OFF_LIFT] = a.lift;
      data[at + ARC_OFF_COLOUR] = a.r;
      data[at + ARC_OFF_COLOUR + 1] = a.g;
      data[at + ARC_OFF_COLOUR + 2] = a.b;
      // A siege hides its heat in the weight channel rather than spending a
      // twelfth float on it: both do the same job in the shader — hotter, thicker
      // — and a fortress at 40% HP has to out-shout the ordinary fire around it.
      data[at + ARC_OFF_WEIGHT] = clamp01(a.weight + a.heat * 0.6);
      data[at + ARC_OFF_BIRTH] = a.birth;
      data[at + ARC_OFF_FLAGS] = a.flags;
    }
    return { data, count: this.arcs.length };
  }

  /**
   * The front glow as instance attributes.
   *
   * `now` fades a cell over its own life, so a front that stopped being fired
   * into goes out rather than sitting lit until it is pruned. Pass nothing and
   * the cells come out at full weight, which is what a test wants.
   */
  packGlow(into?: Float32Array<ArrayBuffer>, now?: number): PackedInstances {
    const need = this.glow.size * GLOW_STRIDE_FLOATS;
    const data = into && into.length >= need ? into : new Float32Array(Math.max(need, 32));
    const t = now !== undefined && this.haveEpoch ? now - this.epoch : null;
    let i = 0;
    // Key order is chunk-major, so the instance list is deterministic frame to
    // frame and an additive pass cannot change its result by reordering.
    for (const g of [...this.glow.values()].sort((a, b) => a.chunk - b.chunk)) {
      const at = i * GLOW_STRIDE_FLOATS;
      const fade = t === null ? 1 : Math.max(0, 1 - (t - g.birth) / GlowMs);
      data[at] = g.x;
      data[at + 1] = g.y;
      data[at + 2] = ChunkSize * 0.85;
      data[at + 3] = g.r;
      data[at + 4] = g.g;
      data[at + 5] = g.b;
      data[at + 6] = g.weight * fade;
      i++;
    }
    return { data, count: this.glow.size };
  }

  /** The scratch buffers the layer reuses, so a frame allocates nothing. */
  packArcsInto(): PackedInstances {
    const p = this.packArcs(this.arcScratch);
    this.arcScratch = p.data;
    return p;
  }

  packGlowInto(now?: number): PackedInstances {
    const p = this.packGlow(this.glowScratch, now);
    this.glowScratch = p.data;
    return p;
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Weight -> 0..1, with a knee.
 *
 * `sqrt` rather than a straight divide: one click against four is the comparison
 * a player is actually making on a front, and a linear map spends almost all of
 * its range on the difference between 200 and 255, which nobody can see.
 */
export function normaliseWeight(w: number, scale: number): number {
  if (w <= 0) return 0;
  return clamp01(Math.sqrt(w / scale));
}

// --- shaders --------------------------------------------------------------

/** Stations along the strip. 24 segments is smooth at any lift the clamp allows. */
export const ArcSegments = 24;

export const ARC_VERT = `#version 300 es
precision highp float;

// Per-vertex: the fixed strip. x = t along the curve, y = which side of it.
layout(location = 0) in vec2 aStrip;
// Per-instance, straight out of the EPHEMERAL typed arrays.
layout(location = 1) in vec2  aP0;
layout(location = 2) in vec2  aP2;
layout(location = 3) in float aLift;
layout(location = 4) in vec3  aColour;
layout(location = 5) in float aWeight;
layout(location = 6) in float aBirth;
layout(location = 7) in float aFlags;

uniform vec2  uViewport;
uniform vec2  uOrigin;
uniform float uScale;
uniform float uNow;        // ms since the field's epoch
uniform float uTracerMs;
uniform float uFadeMs;
uniform float uKillHoldMs;
uniform float uReduced;    // 1 = reduced motion
uniform float uWidthPx;    // base half-width, DEVICE pixels

out float vT;
out float vSide;
out vec3  vColour;
out float vHead;    // where the tracer has got to, 0..1
out float vLifeT;   // 0..1 over the whole life
out float vWeight;
out float vKill;

const uint FLOW_KILL = 1u;
const uint FLOW_SIEGE = 2u;

void main() {
  float t = aStrip.x;
  float side = aStrip.y;
  uint flags = uint(aFlags + 0.5);
  float kill  = (flags & FLOW_KILL)  != 0u ? 1.0 : 0.0;
  float siege = (flags & FLOW_SIEGE) != 0u ? 1.0 : 0.0;

  // The control point, evaluated here rather than uploaded: it is two multiplies
  // and it keeps the instance stride at eleven floats.
  vec2 d = aP2 - aP0;
  float len = length(d);
  vec2 n = len > 1e-5 ? vec2(d.y, -d.x) / len : vec2(0.0, -1.0);
  vec2 p1 = aP0 + d * 0.5 + n * aLift;

  vec2 a = mix(aP0, p1, t);
  vec2 b = mix(p1, aP2, t);
  vec2 pos = mix(a, b, t);
  vec2 tangent = 2.0 * ((1.0 - t) * (p1 - aP0) + t * (aP2 - p1));

  // Expansion happens in DEVICE space, which is the whole reason thickness is
  // zoom-independent and the strip never has to be rebuilt.
  vec2 device = (pos - uOrigin) * uScale;
  vec2 tanDev = tangent * uScale;
  float tl = length(tanDev);
  vec2 nrm = tl > 1e-5 ? vec2(-tanDev.y, tanDev.x) / tl : vec2(0.0, 1.0);

  float life = uTracerMs + uFadeMs + kill * uKillHoldMs;
  float age = uNow - aBirth;
  float lifeT = clamp(age / max(life, 1.0), 0.0, 1.0);
  float head = uReduced > 0.5 ? 1.0 : clamp(age / max(uTracerMs, 1.0), 0.0, 1.0);

  // Thickness carries weight and siege: §15 wants a fortress siege "drawn hotter
  // and thicker as the fortress HP drops", and heat rides in on aWeight.
  float halfPx = uWidthPx * (0.55 + 0.9 * aWeight) * (1.0 + 0.6 * siege);
  // The stroke thins as it fades, so a dying arc reads as a fading tracer rather
  // than as a thick line someone turned the opacity down on.
  halfPx *= (1.0 - 0.45 * lifeT);

  vec2 p = device + nrm * side * halfPx;
  gl_Position = vec4(p.x / uViewport.x * 2.0 - 1.0, 1.0 - p.y / uViewport.y * 2.0, 0.0, 1.0);

  vT = t;
  vSide = side;
  vColour = aColour;
  vHead = head;
  vLifeT = lifeT;
  vWeight = aWeight;
  vKill = kill;
}`;

export const ARC_FRAG = `#version 300 es
precision highp float;

in float vT;
in float vSide;
in vec3  vColour;
in float vHead;
in float vLifeT;
in float vWeight;
in float vKill;

uniform float uReduced;

out vec4 fragColour;

void main() {
  // Across the stroke: a soft core rather than a hard edge, because the blend is
  // additive and a hard edge stacks into visible banding on a busy front.
  float across = 1.0 - abs(vSide);
  float core = across * across;

  // Along the curve: the tracer, and the decaying tail behind it. Under reduced
  // motion the whole stroke is drawn at once — §15 keeps the ARCS and drops the
  // ANIMATION, because the arcs are the information and the animation is not.
  float tail;
  if (uReduced > 0.5) {
    tail = 1.0;
  } else {
    tail = vT <= vHead ? exp(-(vHead - vT) * 7.0) : 0.0;
  }

  // Muzzle flare at p0, on the way out.
  float flare = exp(-vT * 26.0) * (1.0 - smoothstep(0.0, 0.28, vLifeT));
  // Impact bloom at p2, once the tracer has arrived.
  float impact = exp(-(1.0 - vT) * 22.0) * smoothstep(0.85, 1.0, vHead) * (1.0 - smoothstep(0.15, 0.9, vLifeT));

  float fade = 1.0 - smoothstep(0.35, 1.0, vLifeT);
  float energy = tail * 0.9 + flare * 1.3 + impact * (1.4 + vKill * 1.2);
  float alpha = core * energy * fade * (0.35 + 0.65 * vWeight);

  // A kill is brighter, not merely longer-held.
  vec3 colour = vColour * (1.0 + vKill * 0.7);

  if (alpha <= 0.004) discard;
  // Premultiplied, drawn with (ONE, ONE): overlapping tracers ADD, so a contested
  // front self-illuminates with no heat map and no second data channel.
  fragColour = vec4(colour * alpha, alpha);
}`;

export const GLOW_VERT = `#version 300 es
precision highp float;

layout(location = 0) in vec2  aCorner;   // unit quad, -1..1
layout(location = 1) in vec2  aCentre;   // board coordinate
layout(location = 2) in float aRadius;   // board pixels
layout(location = 3) in vec3  aColour;
layout(location = 4) in float aWeight;

uniform vec2  uViewport;
uniform vec2  uOrigin;
uniform float uScale;

out vec2  vLocal;
out vec3  vColour;
out float vWeight;

void main() {
  vLocal = aCorner;
  vColour = aColour;
  vWeight = aWeight;
  vec2 board = aCentre + aCorner * aRadius;
  vec2 device = (board - uOrigin) * uScale;
  gl_Position = vec4(device.x / uViewport.x * 2.0 - 1.0, 1.0 - device.y / uViewport.y * 2.0, 0.0, 1.0);
}`;

export const GLOW_FRAG = `#version 300 es
precision highp float;

in vec2  vLocal;
in vec3  vColour;
in float vWeight;

out vec4 fragColour;

void main() {
  // The arcs the cap refused, as light. Deliberately soft and low: it must read
  // as "this front is hotter than that one", never as a drawn object competing
  // with the tracers it is standing in for.
  float r = length(vLocal);
  float falloff = 1.0 - smoothstep(0.0, 1.0, r);
  float alpha = falloff * falloff * vWeight * 0.5;
  if (alpha <= 0.004) discard;
  fragColour = vec4(vColour * alpha, alpha);
}`;

// --- the draw call --------------------------------------------------------

/**
 * The arc pass: one instanced draw for the tracers, one for the front glow.
 *
 * Drawn after the pixel layer and under the HUD, additive, depth test off — which
 * is §15's rendering paragraph, and the ordering is not arbitrary: additive fire
 * over territory reads as fire, and territory over fire reads as a bug.
 */
export class ArcLayer {
  private readonly gl: WebGL2RenderingContext;
  private readonly arcProgram: WebGLProgram;
  private readonly arcUniforms: Record<string, WebGLUniformLocation | null> = {};
  private readonly arcVAO: WebGLVertexArrayObject;
  private readonly arcStrip: WebGLBuffer;
  private readonly arcInstances: WebGLBuffer;
  private readonly stripVertices: number;

  private readonly glowProgram: WebGLProgram;
  private readonly glowUniforms: Record<string, WebGLUniformLocation | null> = {};
  private readonly glowVAO: WebGLVertexArrayObject;
  private readonly glowInstances: WebGLBuffer;

  private readonly widthPx: number;
  private uploadedRev = -1;

  constructor(gl: WebGL2RenderingContext, opts: { segments?: number; widthPx?: number } = {}) {
    this.gl = gl;
    this.widthPx = opts.widthPx ?? 1.6;
    const segments = opts.segments ?? ArcSegments;
    this.stripVertices = (segments + 1) * 2;

    this.arcProgram = linkProgram(gl, ARC_VERT, ARC_FRAG);
    for (const n of [
      'uViewport',
      'uOrigin',
      'uScale',
      'uNow',
      'uTracerMs',
      'uFadeMs',
      'uKillHoldMs',
      'uReduced',
      'uWidthPx',
    ]) {
      this.arcUniforms[n] = gl.getUniformLocation(this.arcProgram, n);
    }

    const strip = new Float32Array(this.stripVertices * 2);
    for (let s = 0; s <= segments; s++) {
      const t = s / segments;
      strip[s * 4] = t;
      strip[s * 4 + 1] = -1;
      strip[s * 4 + 2] = t;
      strip[s * 4 + 3] = 1;
    }

    this.arcVAO = need(gl.createVertexArray(), 'vertex array');
    gl.bindVertexArray(this.arcVAO);
    this.arcStrip = need(gl.createBuffer(), 'buffer');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.arcStrip);
    gl.bufferData(gl.ARRAY_BUFFER, strip, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    this.arcInstances = need(gl.createBuffer(), 'buffer');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.arcInstances);
    const stride = ARC_STRIDE_FLOATS * 4;
    instanceAttr(gl, 1, 2, stride, ARC_OFF_P0 * 4);
    instanceAttr(gl, 2, 2, stride, ARC_OFF_P2 * 4);
    instanceAttr(gl, 3, 1, stride, ARC_OFF_LIFT * 4);
    instanceAttr(gl, 4, 3, stride, ARC_OFF_COLOUR * 4);
    instanceAttr(gl, 5, 1, stride, ARC_OFF_WEIGHT * 4);
    instanceAttr(gl, 6, 1, stride, ARC_OFF_BIRTH * 4);
    instanceAttr(gl, 7, 1, stride, ARC_OFF_FLAGS * 4);
    gl.bindVertexArray(null);

    this.glowProgram = linkProgram(gl, GLOW_VERT, GLOW_FRAG);
    for (const n of ['uViewport', 'uOrigin', 'uScale']) {
      this.glowUniforms[n] = gl.getUniformLocation(this.glowProgram, n);
    }
    this.glowVAO = need(gl.createVertexArray(), 'vertex array');
    gl.bindVertexArray(this.glowVAO);
    const corners = need(gl.createBuffer(), 'buffer');
    gl.bindBuffer(gl.ARRAY_BUFFER, corners);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.glowInstances = need(gl.createBuffer(), 'buffer');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.glowInstances);
    const gstride = GLOW_STRIDE_FLOATS * 4;
    instanceAttr(gl, 1, 2, gstride, 0);
    instanceAttr(gl, 2, 1, gstride, 8);
    instanceAttr(gl, 3, 3, gstride, 12);
    instanceAttr(gl, 4, 1, gstride, 24);
    gl.bindVertexArray(null);
  }

  draw(field: ArcField, camera: Camera, now: number, reducedMotion = false): void {
    const gl = this.gl;
    const { width, height } = camera.viewport;
    const arcs = field.packArcsInto();
    const glow = field.packGlowInto(now);
    if (arcs.count === 0 && glow.count === 0) return;

    gl.enable(gl.BLEND);
    // Additive, premultiplied. The fragment shaders emit colour*alpha.
    gl.blendFunc(gl.ONE, gl.ONE);

    if (glow.count > 0) {
      gl.useProgram(this.glowProgram);
      gl.uniform2f(this.glowUniforms.uViewport!, width, height);
      gl.uniform2f(this.glowUniforms.uOrigin!, camera.originX, camera.originY);
      gl.uniform1f(this.glowUniforms.uScale!, camera.scale);
      gl.bindVertexArray(this.glowVAO);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.glowInstances);
      gl.bufferData(gl.ARRAY_BUFFER, glow.data.subarray(0, glow.count * GLOW_STRIDE_FLOATS), gl.DYNAMIC_DRAW);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, glow.count);
    }

    if (arcs.count > 0) {
      gl.useProgram(this.arcProgram);
      const u = this.arcUniforms;
      gl.uniform2f(u.uViewport!, width, height);
      gl.uniform2f(u.uOrigin!, camera.originX, camera.originY);
      gl.uniform1f(u.uScale!, camera.scale);
      gl.uniform1f(u.uNow!, field.clock(now));
      gl.uniform1f(u.uTracerMs!, TracerMs);
      gl.uniform1f(u.uFadeMs!, ArcFadeMs);
      gl.uniform1f(u.uKillHoldMs!, KillHoldMs);
      gl.uniform1f(u.uReduced!, reducedMotion ? 1 : 0);
      gl.uniform1f(u.uWidthPx!, this.widthPx);
      gl.bindVertexArray(this.arcVAO);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.arcInstances);
      // The set only changes when a tick lands or an arc dies, so the upload is
      // keyed on the revision rather than done every frame.
      if (this.uploadedRev !== field.revision()) {
        gl.bufferData(gl.ARRAY_BUFFER, arcs.data.subarray(0, arcs.count * ARC_STRIDE_FLOATS), gl.DYNAMIC_DRAW);
        this.uploadedRev = field.revision();
      }
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, this.stripVertices, arcs.count);
    }

    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteProgram(this.arcProgram);
    gl.deleteProgram(this.glowProgram);
    gl.deleteVertexArray(this.arcVAO);
    gl.deleteVertexArray(this.glowVAO);
    gl.deleteBuffer(this.arcStrip);
    gl.deleteBuffer(this.arcInstances);
    gl.deleteBuffer(this.glowInstances);
  }
}

function instanceAttr(
  gl: WebGL2RenderingContext,
  loc: number,
  size: number,
  stride: number,
  offset: number,
): void {
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, offset);
  gl.vertexAttribDivisor(loc, 1);
}

function need<T>(v: T | null, what: string): T {
  if (v === null) throw new Error(`render: could not create a ${what}`);
  return v;
}

function linkProgram(gl: WebGL2RenderingContext, vert: string, frag: string): WebGLProgram {
  const v = compileShader(gl, gl.VERTEX_SHADER, vert);
  const f = compileShader(gl, gl.FRAGMENT_SHADER, frag);
  const p = need(gl.createProgram(), 'program');
  gl.attachShader(p, v);
  gl.attachShader(p, f);
  gl.linkProgram(p);
  gl.deleteShader(v);
  gl.deleteShader(f);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(p);
    gl.deleteProgram(p);
    throw new Error(`render: arc program link failed: ${log}`);
  }
  return p;
}

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = need(gl.createShader(type), 'shader');
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`render: arc shader compile failed: ${log}`);
  }
  return sh;
}
